import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useMyCity } from "@/hooks/useMyCity";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WorldMap } from "@/components/map/WorldMap";
import { CountryPanel } from "@/components/map/CountryPanel";
import { BeenThere } from "./BeenThere";
import { WantToGo } from "./WantToGo";
import { Timeline } from "./Timeline";
import { Sparkles } from "lucide-react";
import { StoryDialog } from "@/components/story/StoryDialog";

export const TravelSection = ({ geo, data, b }) => {
  const [selected, setSelected] = useState(null);
  const [focus, setFocus] = useState(null);
  const onFocus = (name, cities) => { setSelected(name); setFocus({ name, cities, t: Date.now() }); };
  const visitedEntry = data.visited.find((v) => v.country === selected);
  const wishIndex = data.wishlist.findIndex((w) => w.country === selected);
  useEffect(() => {
    const on = ({ detail: d }) => (d.open ? onFocus(d.name, d.cities) : (setSelected(null), setFocus({ name: d.name, cities: d.cities, t: Date.now() })));
    window.addEventListener("bl:focus", on);
    return () => window.removeEventListener("bl:focus", on);
  }, []);
  const owner = !b.readOnly;
  const { data: locs = [] } = useQuery({ queryKey: ["friend-locations"], queryFn: api.friendLocations, enabled: owner, refetchInterval: 120000 });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings, enabled: owner });
  const pins = useMemo(() => locs.filter((p) => !(settings?.map_hidden || []).includes(p.friend_id)), [locs, settings]);
  const { me, toggle: toggleMe } = useMyCity(owner);

  return (
    <section id="travel" className="scroll-mt-20 px-4 sm:px-8 lg:px-12 pt-24 pb-10" data-testid="travel-section">
      <div className="mb-8 animate-rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Section 01</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-2">{data.owner ? `${data.owner.name.split(" ")[0]}'s world, ` : "The world, "}<span className="text-emerald-400">so far.</span></h1>
        </div>
        {!b.readOnly && geo && (
          <StoryDialog geo={geo} data={data} b={b}>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-400 via-teal-300 to-sky-400 text-[#04120c] font-medium hover:shadow-[0_0_30px_rgba(52,211,153,0.35)] transition-shadow duration-300" data-testid="story-open-button">
              <Sparkles size={16} /> Create a story
            </button>
          </StoryDialog>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 h-[460px] sm:h-[560px] lg:h-[680px] relative rounded-2xl border border-white/10 bg-[#0D131F] overflow-hidden shadow-2xl">
          {geo ? (
            <WorldMap geo={geo} visited={data.visited} wishlist={data.wishlist} selected={selected} onSelect={setSelected} focus={focus}
              onCity={(c) => setSelected(c.c)} pins={owner ? pins : []} me={owner ? me : null}
              onLocate={owner ? async () => { const c = await toggleMe(); if (c) { setSelected(null); setFocus({ name: c.country, cities: [c.city], t: Date.now() }); } } : undefined} />
          ) : (
            <div className="h-full grid place-items-center font-mono text-xs text-slate-500" data-testid="map-loading">Charting the world…</div>
          )}
          {selected && geo && (
            <CountryPanel key={selected} name={selected} cities={geo.cities} visitedEntry={visitedEntry} wishIndex={wishIndex} onClose={() => setSelected(null)} b={b} />
          )}
        </div>
        <aside className="lg:col-span-4 rounded-2xl border border-white/10 bg-[#131A26] p-5 lg:h-[680px] flex flex-col">
          <Tabs defaultValue="wish" className="flex flex-col min-h-0 flex-1">
            <TabsList className="grid grid-cols-2 bg-white/5">
              <TabsTrigger value="wish" data-testid="tab-want-to-go" className="data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-200">Want to go · {data.wishlist.length}</TabsTrigger>
              <TabsTrigger value="been" data-testid="tab-been-there" className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-200">Been there · {data.visited.length}</TabsTrigger>
            </TabsList>
            <div className="flex-1 min-h-0 overflow-y-auto mt-4 pr-1">
              <TabsContent value="wish" className="mt-0"><WantToGo wishlist={data.wishlist} names={geo?.names || []} b={b} onFocus={onFocus} beenTo={data.visited} /></TabsContent>
              <TabsContent value="been" className="mt-0"><BeenThere visited={data.visited} names={geo?.names || []} b={b} onFocus={onFocus} /></TabsContent>
            </div>
          </Tabs>
        </aside>
      </div>
      <Timeline visited={data.visited} onFocus={onFocus} />
    </section>
  );
};
